import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthContext from './auth';

const ThemeContext = createContext(null);

// Storage keys
const MANUAL_THEME_KEY = 'yulontap-theme-preference'; // Persists across sessions (user's explicit choice)
const PROMPT_DISMISSED_DATE_KEY = 'yulontap-evening-prompt-date'; // Track when prompt was last dismissed

// Check if current time is between 8PM and 6AM
const isEveningTime = () => {
    const hour = new Date().getHours();
    return hour >= 20 || hour < 6; // 8PM (20:00) to 6AM (06:00)
};

// Get today's date string for comparison
const getTodayDateString = () => {
    return new Date().toDateString();
};

// Get initial theme from storage (runs once on load)
const getInitialTheme = () => {
    const manualPref = localStorage.getItem(MANUAL_THEME_KEY);
    if (manualPref) {
        return { theme: manualPref, isManual: true };
    }
    return { theme: 'light', isManual: false };
};

const initialState = getInitialTheme();

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(initialState.theme);
    const [showEveningPrompt, setShowEveningPrompt] = useState(false);
    const [isManualPreference, setIsManualPreference] = useState(initialState.isManual);
    // P_THEME_PERSISTENCE_1.0 — read auth context directly (NOT via
    // useAuth) because ThemeProvider wraps AuthProvider in App.js, so
    // useAuth would throw with "must be used within an AuthProvider".
    // The raw context returns null at first render and then the real
    // value once AuthProvider mounts — both states are handled below.
    const authCtx = useContext(AuthContext);
    const user = authCtx?.user || null;
    const isAuthenticated = authCtx?.isAuthenticated || false;
    const setPreferredTheme = authCtx?.setPreferredTheme;

    // Apply theme to document
    useEffect(() => {
        const root = document.documentElement;
        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
    }, [theme]);

    // P_THEME_PERSISTENCE_1.0 — when the server returns the user's
    // owned `preferred_theme`, adopt it as the authoritative value.
    // This handles the case where iPad Safari ITP wiped localStorage
    // but the server still remembers the user's choice.
    useEffect(() => {
        const serverTheme = user?.preferred_theme;
        if (
            typeof serverTheme === 'string' &&
            (serverTheme === 'light' || serverTheme === 'dark') &&
            serverTheme !== theme
        ) {
            setTheme(serverTheme);
            setIsManualPreference(true);
            try { localStorage.setItem(MANUAL_THEME_KEY, serverTheme); } catch (_e) { /* private mode */ }
        }
    }, [user?.preferred_theme, theme]);

    // Check if we should show the evening prompt (called after login)
    const checkEveningPrompt = useCallback(() => {
        const manualPref = localStorage.getItem(MANUAL_THEME_KEY);
        const lastPromptDate = localStorage.getItem(PROMPT_DISMISSED_DATE_KEY);
        const today = getTodayDateString();

        // Only show prompt if:
        // 1. User has NOT set a manual preference (they explicitly chose light or dark)
        // 2. OR user is currently in light mode (manual preference is 'light')
        // 3. It's evening time
        // 4. Prompt hasn't been dismissed today
        const isInLightMode = !manualPref || manualPref === 'light';
        const promptNotShownToday = lastPromptDate !== today;
        
        if (isInLightMode && isEveningTime() && promptNotShownToday) {
            setShowEveningPrompt(true);
        }
    }, []);

    // Handle manual toggle (persists permanently)
    const toggleTheme = useCallback(() => {
        const newTheme = theme === 'light' ? 'dark' : 'light';
        setTheme(newTheme);
        setIsManualPreference(true);
        try { localStorage.setItem(MANUAL_THEME_KEY, newTheme); } catch (_e) { /* private mode */ }
        // P_THEME_PERSISTENCE_1.0 — push the choice up to the server
        // when the user is signed in. Fire-and-forget; AuthProvider
        // handles its own failure fallback. Anonymous users (login
        // page) just get localStorage persistence as before.
        if (isAuthenticated && typeof setPreferredTheme === 'function') {
            setPreferredTheme(newTheme);
        }
    }, [theme, isAuthenticated, setPreferredTheme]);

    // Handle evening prompt response
    const handleEveningPromptResponse = useCallback((acceptDarkMode) => {
        localStorage.setItem(PROMPT_DISMISSED_DATE_KEY, getTodayDateString());
        setShowEveningPrompt(false);

        if (acceptDarkMode) {
            // User accepts dark mode - save as their preference
            setTheme('dark');
            setIsManualPreference(true);
            try { localStorage.setItem(MANUAL_THEME_KEY, 'dark'); } catch (_e) { /* private mode */ }
            // Mirror to server when authenticated — same contract as
            // the manual toggle.
            if (isAuthenticated && typeof setPreferredTheme === 'function') {
                setPreferredTheme('dark');
            }
        }
        // If declined, keep current theme (light)
    }, [isAuthenticated, setPreferredTheme]);

    // Dismiss prompt without choosing (will ask again tomorrow if still in light mode)
    const dismissEveningPrompt = useCallback(() => {
        localStorage.setItem(PROMPT_DISMISSED_DATE_KEY, getTodayDateString());
        setShowEveningPrompt(false);
    }, []);

    // Clear session flags on logout (keep the theme preference though!)
    const clearSessionTheme = useCallback(() => {
        // Don't clear the theme preference - it should persist across sessions
        // Just reload the saved preference
        const manualPref = localStorage.getItem(MANUAL_THEME_KEY);
        if (manualPref) {
            setTheme(manualPref);
            setIsManualPreference(true);
        } else {
            setTheme('light');
            setIsManualPreference(false);
        }
    }, []);

    return (
        <ThemeContext.Provider value={{
            theme,
            toggleTheme,
            showEveningPrompt,
            checkEveningPrompt,
            handleEveningPromptResponse,
            dismissEveningPrompt,
            clearSessionTheme,
            isManualPreference
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export default ThemeContext;
